import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface AccountFormData {
  business: string;
  domain: string;
  domainOption: string;
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  confirmPassword: string;
  securityQuestion: string;
  securityAnswer: string;
  email: string;
  phone: string;
}

export interface AddressFormData {
  street?: string;
  number?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

export interface ProductData {
  id: string;
  name: string;
  quantity: number;
  price: number;
}

export interface CloudAccountData {
  email: string;
  username?: string;
  password?: string;
  confirmPassword?: string;
  sessionId?: string;
  verified?: boolean;
}

export interface CloudAddressData {
  street: string;
  country: string;
  state: string;
  city: string;
  postalCode?: string;
  business?: string;
  firstName: string;
  lastName: string;
  phone: string;
  currency?: string;
  usCountry?: string;
}

export interface PhoneMeta {
  phone?: string;
  phoneCountryCode?: string;
  phoneDialCode?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FormDataService {
  // --- Products ---
  private productsData: ProductData[] = [];

  // --- General Account ---
  private accountFormDataSubject = new BehaviorSubject<AccountFormData | null>(null);
  accountFormData$ = this.accountFormDataSubject.asObservable();

  // --- General Address ---
  private addressFormDataSubject = new BehaviorSubject<AddressFormData | null>(null);
  addressFormData$ = this.addressFormDataSubject.asObservable();

  // --- Cloud Account ---
  private cloudAccountDataSubject = new BehaviorSubject<CloudAccountData | null>(null);
  cloudAccountData$ = this.cloudAccountDataSubject.asObservable();

  // --- Cloud Address ---
  private cloudAddressDataSubject = new BehaviorSubject<CloudAddressData | null>(null);
  cloudAddressData$ = this.cloudAddressDataSubject.asObservable();

  // --- Captcha ---
  private captchaTokenSubject = new BehaviorSubject<string | null>(null);
  captchaToken$ = this.captchaTokenSubject.asObservable();
  private captchaResetSubject = new BehaviorSubject<boolean>(false);
  captchaReset$ = this.captchaResetSubject.asObservable();

  // --- Device Fingerprint ---
  private deviceFingerPrintSubject = new BehaviorSubject<string | null>(null);
  deviceFingerPrint$ = this.deviceFingerPrintSubject.asObservable();

  // --- Phone meta (phone value + selected country code) ---
  private phoneMetaSubject = new BehaviorSubject<PhoneMeta>({});
  phoneMeta$ = this.phoneMetaSubject.asObservable();

  // --- Rack UID ---
  private rackUidSubject = new BehaviorSubject<string | null>(null);
  rackUid$ = this.rackUidSubject.asObservable();

  constructor() {
    const fingerprint = this.generateFingerPrint();
    this.deviceFingerPrintSubject.next(fingerprint);

    const storedRackUid = this.getCookie('rackUID');
    if (storedRackUid) {
      this.rackUidSubject.next(storedRackUid);
    } else {
      const newRackUid = this.generateRackUid(fingerprint);
      this.setRackUid(newRackUid);
    }
  }

  // --- Products ---
  setProductsData(data: ProductData[]) {
    this.productsData = data;
  }
  getProductsData(): ProductData[] {
    return this.productsData;
  }

  // --- General Account ---
  setAccountData(data: AccountFormData) {
    this.accountFormDataSubject.next(data);
  }
  getAccountData(): AccountFormData | null {
    return this.accountFormDataSubject.value;
  }

  // --- General Address ---
  setAddressData(data: AddressFormData) {
    this.addressFormDataSubject.next(data);
  }
  getAddressData(): AddressFormData | null {
    return this.addressFormDataSubject.value;
  }

  // --- Cloud Account ---
  setCloudAccountData(data: Partial<CloudAccountData>) {
    const current = this.cloudAccountDataSubject.value || {} as CloudAccountData;
    this.cloudAccountDataSubject.next({ ...current, ...data } as CloudAccountData);
  }

  getCloudAccountData(): CloudAccountData | null {
    return this.cloudAccountDataSubject.value;
  }
  clearCloudAccountData() {
    this.cloudAccountDataSubject.next(null);
  }

  // --- Cloud Address ---
  setCloudAddressData(data: Partial<CloudAddressData>) {
    const current = this.cloudAddressDataSubject.value || {} as CloudAddressData;
    this.cloudAddressDataSubject.next({ ...current, ...data } as CloudAddressData);
  }

  getCloudAddressData(): CloudAddressData | null {
    return this.cloudAddressDataSubject.value;
  }
  clearCloudAddressData() {
    this.cloudAddressDataSubject.next(null);
  }

  // --- Phone meta (phone value + selected country code) ---
  setPhoneMeta(data: Partial<PhoneMeta>) {
    const current = this.phoneMetaSubject.value || {};
    this.phoneMetaSubject.next({ ...current, ...data });
  }

  getPhoneMeta(): PhoneMeta {
    return this.phoneMetaSubject.value;
  }

  // --- Captcha ---
  setCaptchaToken(token: string | null) {
    this.captchaTokenSubject.next(token);
  }

  getCaptchaToken(): string | null {
    return this.captchaTokenSubject.value;
  }

  resetCaptcha() {
    this.captchaTokenSubject.next(null);
    this.captchaResetSubject.next(true);
  }

  private captchaExecuteSubject = new BehaviorSubject<void>(undefined);
    captchaExecute$ = this.captchaExecuteSubject.asObservable();

    triggerCaptchaExecute() {
      this.captchaExecuteSubject.next();
    }

  // --- Device Fingerprint ---
  private generateFingerPrint(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }
  getDeviceFingerPrint(): string | null {
    return this.deviceFingerPrintSubject.value;
  }

  // --- RackUID ---
  private generateRackUid(seed: string): string {
    return `US${seed}`;
  }
  setRackUid(value: string) {
    this.rackUidSubject.next(value);
    this.setCookie('rackUID', value, 365 * 20);
  }
  getRackUid(): string | null {
    return this.rackUidSubject.value;
  }

  // --- Cookies ---
  private setCookie(name: string, value: string, days: number) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; domain=.rackspace.com`;
  }
  private getCookie(name: string): string | null {
    return document.cookie
      .split('; ')
      .find(row => row.startsWith(name + '='))
      ?.split('=')[1] || null;
  }

  // --- Reset all ---
  clearAll() {
    this.productsData = [];
    this.accountFormDataSubject.next(null);
    this.addressFormDataSubject.next(null);
    this.cloudAccountDataSubject.next(null);
    this.cloudAddressDataSubject.next(null);
    this.captchaTokenSubject.next(null);

    const newRackUid = this.generateRackUid(this.generateFingerPrint());
    this.setRackUid(newRackUid);
  }
}
